import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ImagePlus,
  Trophy,
  CalendarDays,
  MapPin,
  Users,
  ShoppingBag,
  X,
  ChevronRight,
} from 'lucide-react';

// Gleiche Optionen wie das CreateEntryModal der Flutter-App
const OPTIONS = [
  // GANZ OBEN (Marcel 2026-08-31): Social-Media-Beitrag — öffnet den
  // Composer über den ?neu=1-Query-Param der Feed-Seite
  {
    icon: ImagePlus,
    label: 'Beitrag',
    description: 'Bilder & Text für den Community-Feed',
    to: '/feed?neu=1',
  },
  {
    icon: Trophy,
    label: 'Turnier',
    description: 'Erstelle ein neues Turnier',
    to: '/verwaltung/turnier/erstellen',
  },
  {
    icon: CalendarDays,
    label: 'Turnierserie',
    description: 'Erstelle eine neue Turnierserie',
    to: '/verwaltung/turnierserie/erstellen',
  },
  {
    icon: MapPin,
    label: 'Spielstätte',
    description: 'Erstelle eine neue Spielstätte',
    to: '/verwaltung/spielstaette/erstellen',
  },
  {
    icon: Users,
    label: 'Verein',
    description: 'Erstelle einen neuen Verein',
    to: '/verwaltung/verein/erstellen',
  },
  {
    icon: ShoppingBag,
    label: 'Shop',
    description: 'Erstelle einen neuen Shop',
    to: '/verwaltung/shop/erstellen',
  },
];

interface CreateEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Auswahl-Modal "Neuer Eintrag" — Pendant zum CreateEntryModal der
 * Flutter-App: Mobile als Vollbild, ab sm: zentriertes Modal.
 */
const CreateEntryModal: React.FC<CreateEntryModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const select = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-zinc-950 sm:items-center sm:justify-center sm:bg-black/70 sm:p-4 sm:backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Neuer Eintrag"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-8 pt-16 sm:min-h-fit sm:flex-none sm:w-full sm:max-w-md sm:rounded-2xl sm:border sm:border-zinc-800 sm:bg-zinc-900 sm:px-6 sm:pb-6 sm:pt-8">
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:bg-zinc-800 hover:text-white"
        >
          <X size={20} />
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-bold tracking-tight text-white">Neuer Eintrag</h2>
          <p className="mt-1 text-sm text-gray-400">Wähle den Typ des Eintrags</p>
        </div>

        <div className="space-y-2">
          {OPTIONS.map(({ icon: Icon, label, description, to }) => (
            <button
              key={to}
              type="button"
              onClick={() => select(to)}
              className="flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-left hover:border-[#3fced1]/50 sm:bg-zinc-950/40"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#3fced1]/20 bg-[#3fced1]/10">
                <Icon size={18} className="text-[#3fced1]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-white">{label}</span>
                <span className="block text-xs text-gray-400">{description}</span>
              </span>
              <ChevronRight size={16} className="shrink-0 text-gray-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreateEntryModal;
